import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { PricingMode, supportsBPSPricing, type Security } from '@talos/kyoko';
import { useCallback } from 'react';

/**
 * Hook that returns a function to determine the appropriate PricingMode for priming.
 *
 * Returns a getter function that can be called inside callbacks (e.g., click handlers)
 * to get the pricing mode for the security passed to the hook.
 *
 * Note: This hook handles BPS pricing mode only. Options priming
 * is handled separately by `useOptionChainPrimeOms`.
 *
 * @param security - The security being traded
 * @returns A function that returns the PricingMode to use:
 *   - `PricingMode.BPS` when security supports BPS and user has BPS display enabled
 *   - `PricingMode.Default` when security supports BPS but user has BPS display disabled
 *   - `undefined` when security doesn't support BPS pricing
 */
export function useGetOrderPricingMode(security: Security | undefined) {
  const { showDelta1BPS } = useDisplaySettings();

  const getOrderPricingMode = useCallback((): PricingMode | undefined => {
    if (!supportsBPSPricing(security)) {
      return undefined;
    }

    return showDelta1BPS ? PricingMode.BPS : PricingMode.Default;
  }, [security, showDelta1BPS]);

  return { getOrderPricingMode };
}
